import { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useRoutes, useAutoScroll } from '../../hooks';
import { useConfigStore } from '@/store';
import { SortableRouteCard } from './SortableRouteCard';

export function RouteGrid() {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLElement>(null);
  const { data: routes, isLoading, error } = useRoutes();
  const { hideRoute, routesPerRow, updateRouteOrder } = useConfigStore();

  // Auto-scroll
  useAutoScroll(containerRef);

  // Drag-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id && routes) {
        const oldIndex = routes.findIndex((r) => r.global_route_id === active.id);
        const newIndex = routes.findIndex((r) => r.global_route_id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(
            routes.map((r) => r.global_route_id),
            oldIndex,
            newIndex
          );
          updateRouteOrder(newOrder);
        }
      }
    },
    [routes, updateRouteOrder]
  );

  // Update CSS variable for routes per row
  const gridStyle = {
    '--routes-per-row': routesPerRow,
  } as React.CSSProperties;

  const routesClasses = "absolute inset-0 overflow-y-auto scroll-smooth flex flex-wrap content-start";
  const emptyClasses = "w-full text-center py-16 px-8 text-2xl text-[#666666]";

  if (isLoading && !routes) {
    return (
      <section className={routesClasses} style={gridStyle}>
        <div className={emptyClasses}>{t('loading')}</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={routesClasses} style={gridStyle}>
        <div className={emptyClasses}>{t('error')}</div>
      </section>
    );
  }

  if (!routes || routes.length === 0) {
    return (
      <section className={routesClasses} style={gridStyle}>
        <div className={emptyClasses}>{t('no_routes')}</div>
      </section>
    );
  }

  const routeIds = routes.map((r) => r.global_route_id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={routeIds} strategy={rectSortingStrategy}>
        <section ref={containerRef} className={routesClasses} style={gridStyle}>
          {routes.map((route) => (
            <SortableRouteCard
              key={route.global_route_id}
              route={route}
              onHide={hideRoute}
            />
          ))}
        </section>
      </SortableContext>
    </DndContext>
  );
}
